/**
 * Analytics Service - Direct Fetch Implementation
 * 
 * This service uses direct fetch calls instead of Apollo hooks to avoid
 * React 19 compatibility issues with Apollo Client's useMutation hook.
 */

interface UserActivityEvent {
  eventType: string;
  pageType?: string;
  trackName?: string;
  raceNumber?: number;
  selectedDate: string;
  metadata?: any;
  timestamp: string;
  sessionId?: string;
}

interface AnalyticsConfig {
  batchSize: number;
  batchInterval: number; // milliseconds
  enabled: boolean;
}

const DEFAULT_CONFIG: AnalyticsConfig = {
  batchSize: 10,
  batchInterval: 10000, // 10 seconds
  enabled: true
};

// Generate or retrieve session ID
const getSessionId = (): string => {
  const SESSION_KEY = 'analytics_session_id';
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  
  return sessionId;
};

class AnalyticsService {
  private config: AnalyticsConfig;
  private eventQueue: UserActivityEvent[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private isFlushingRef = false;
  private sessionId: string;

  constructor(config: Partial<AnalyticsConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = getSessionId();
    
    // Set up page unload handlers
    this.setupUnloadHandlers();
  }

  private setupUnloadHandlers(): void {
    // Page unload handler with sendBeacon fallback
    const handleBeforeUnload = () => {
      if (this.eventQueue.length > 0) {
        this.flushEventsSync();
      }
    };

    // Visibility change handler (when user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && this.eventQueue.length > 0) {
        this.flushEvents();
      }
    };

    // Route change handler (for SPA navigation)
    const handlePopState = () => {
      if (this.eventQueue.length > 0) {
        this.flushEvents();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('popstate', handlePopState);
  }

  private async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0 || this.isFlushingRef || !this.config.enabled) {
      return;
    }
    
    this.isFlushingRef = true;
    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || 
        (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');

      // Get authentication token from localStorage (where Amplify stores it)
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      // Try to get the authentication token
      try {
        // Check for Amplify token in localStorage
        const tokenKey = Object.keys(localStorage).find(key => 
          key.includes('CognitoIdentityServiceProvider') && key.includes('idToken')
        );
        
        if (tokenKey) {
          const token = localStorage.getItem(tokenKey);
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
            console.log('📊 Analytics: Including auth token for user identification');
          }
        }
      } catch (authError) {
        console.warn('Analytics: Could not retrieve auth token:', authError);
        // Continue without auth - will be logged as anonymous
      }

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: `
            mutation LogUserActivity($events: [UserActivityInput!]!) {
              logUserActivity(events: $events) {
                success
                eventsLogged
                errors
              }
            }
          `,
          variables: { events: eventsToSend }
        })
      });

      const result = await response.json();

      if (result.data?.logUserActivity?.success) {
        console.log(`✅ Analytics: Sent ${eventsToSend.length} events successfully`);
      } else {
        console.warn('⚠️ Analytics: Some events may not have been logged:', result.data?.logUserActivity?.errors);
        // Re-queue failed events (optional - could lead to infinite retry)
        // this.eventQueue.unshift(...eventsToSend);
      }
    } catch (error) {
      console.error('❌ Analytics: Failed to send events:', error);
      
      // Re-queue failed events for retry (optional)
      this.eventQueue.unshift(...eventsToSend);
    } finally {
      this.isFlushingRef = false;
    }
  }

  private flushEventsSync(): void {
    if (this.eventQueue.length === 0 || !this.config.enabled) {
      return;
    }

    const eventsToSend = [...this.eventQueue];
    // Cleared now: visibilitychange ('hidden') fires right after beforeunload
    // and would otherwise send the same events a second time.
    this.eventQueue = [];
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    try {
      // Use sendBeacon if available (more reliable on page unload)
      if (navigator.sendBeacon) {
        // Get authentication token for sendBeacon
        let authToken = null;
        try {
          const tokenKey = Object.keys(localStorage).find(key => 
            key.includes('CognitoIdentityServiceProvider') && key.includes('idToken')
          );
          if (tokenKey) {
            authToken = localStorage.getItem(tokenKey);
          }
        } catch (authError) {
          // Continue without auth
        }

        const payload = JSON.stringify({
          query: `
            mutation LogUserActivity($events: [UserActivityInput!]!) {
              logUserActivity(events: $events) {
                success
                eventsLogged
                errors
              }
            }
          `,
          variables: { events: eventsToSend }
        });
        
        const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || '/graphql';
        
        // Note: sendBeacon doesn't support custom headers, so we'll include auth in the payload
        // This is a limitation of sendBeacon - for authenticated requests, the regular fetch is better
        if (authToken) {
          // For authenticated users, try a synchronous fetch instead of sendBeacon
          fetch(graphqlEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: payload,
            keepalive: true // This helps ensure the request completes even during page unload
          }).catch(() => {
            // Fallback to sendBeacon without auth
            navigator.sendBeacon(graphqlEndpoint, payload);
          });
        } else {
          navigator.sendBeacon(graphqlEndpoint, payload);
        }
      }
    } catch (error) {
      console.warn('Analytics: Failed to send events on page unload:', error);
    }
  }

  public trackEvent(eventType: string, data: Partial<UserActivityEvent> = {}): void {
    if (!this.config.enabled) {
      return;
    }

    const event: UserActivityEvent = {
      eventType,
      selectedDate: data.selectedDate || new Date().toISOString().split('T')[0],
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      ...data
    };

    this.eventQueue.push(event);

    // Clear existing timer
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    // Set new timer for batch interval
    this.batchTimer = setTimeout(() => {
      this.flushEvents();
    }, this.config.batchInterval);

    // Immediate flush if queue reaches batch size
    if (this.eventQueue.length >= this.config.batchSize) {
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
      }
      this.flushEvents();
    }
  }

  public trackPageView(pageType: string, trackName?: string, selectedDate?: string, metadata?: any): void {
    this.trackEvent('page_view', {
      pageType,
      trackName,
      selectedDate,
      metadata
    });
  }

  public trackContentExpand(pageType: string, trackName?: string, raceNumber?: number, selectedDate?: string, metadata?: any): void {
    this.trackEvent('content_expand', {
      pageType,
      trackName,
      raceNumber,
      selectedDate,
      metadata
    });
  }

  public trackContentCollapse(pageType: string, trackName?: string, raceNumber?: number, selectedDate?: string, metadata?: any): void {
    this.trackEvent('content_collapse', {
      pageType,
      trackName,
      raceNumber,
      selectedDate,
      metadata
    });
  }

  public trackShare(shareMethod: string, pageType: string, trackName?: string, selectedDate?: string, metadata?: any): void {
    this.trackEvent('share_click', {
      pageType,
      trackName,
      selectedDate,
      metadata: {
        shareMethod,
        ...metadata
      }
    });
  }

  public trackLogin(success: boolean, metadata?: any): void {
    this.trackEvent(success ? 'login_success' : 'login_failure', {
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackLogout(metadata?: any): void {
    this.trackEvent('logout', {
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackRegistration(metadata?: any): void {
    this.trackEvent('registration_complete', {
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  // Registration funnel tracking methods
  public trackRegistrationPageView(metadata?: any): void {
    this.trackEvent('registration_page_view', {
      pageType: 'registration',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackRegistrationFormStarted(metadata?: any): void {
    this.trackEvent('registration_form_started', {
      pageType: 'registration',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackRegistrationFormFieldCompleted(fieldName: string, metadata?: any): void {
    this.trackEvent('registration_form_field_completed', {
      pageType: 'registration',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata: {
        fieldName,
        ...metadata
      }
    });
  }

  public trackRegistrationFormSubmitted(metadata?: any): void {
    this.trackEvent('registration_form_submitted', {
      pageType: 'registration',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackRegistrationSuccess(metadata?: any): void {
    this.trackEvent('registration_success', {
      pageType: 'registration',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackRegistrationAbandoned(metadata?: any): void {
    this.trackEvent('registration_abandoned', {
      pageType: 'registration',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackLoginAttempt(metadata?: any): void {
    this.trackEvent('login_attempt', {
      pageType: 'login',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public trackLoginPageView(metadata?: any): void {
    this.trackEvent('login_page_view', {
      pageType: 'login',
      selectedDate: new Date().toISOString().split('T')[0],
      metadata
    });
  }

  public async flushEventsManually(): Promise<void> {
    return this.flushEvents();
  }

  public getQueueSize(): number {
    return this.eventQueue.length;
  }

  public getConfig(): AnalyticsConfig {
    return { ...this.config };
  }
}

// Create a singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
export { AnalyticsService };
export type { UserActivityEvent, AnalyticsConfig };
